import { useState, useEffect } from "react";
import type { MouseEvent } from "react";
import Switch from "@/components/button.tsx";

const Navbar = (
  { onLanguageChangePath, onLanguageSelected, navigationData }:
    {
      onLanguageChangePath?: (path: string) => void,
      onLanguageSelected?: (isEnglish: string) => void,
      navigationData?: any
    }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const path = window.location.pathname;
  const lang = path.startsWith('/zh') ? false : true;
  const [isEnglish, setIsEnglish] = useState(lang);
  const initialLang = isEnglish ? "/en" : "/zh";
  const [currentLanguage, setCurrentLanguage] = useState(initialLang);

  const handleLanguage = () => {
    const newIsEnglish = !isEnglish;
    const currentPath = window.location.pathname;

    // Replace the language prefix in the current path
    const newLangPrefix = newIsEnglish ? '/en' : '/zh';
    const oldLangPrefix = newIsEnglish ? '/zh' : '/en';
    const newPath = currentPath.startsWith(oldLangPrefix)
      ? currentPath.replace(oldLangPrefix, newLangPrefix)
      : newLangPrefix + currentPath;

    setIsEnglish(newIsEnglish);
    setCurrentLanguage(newLangPrefix);
    window.history.replaceState({}, '', newPath);
    onLanguageChangePath?.(newPath);
    onLanguageSelected?.(newLangPrefix.replace('/', ''));
  };

  let currentLocation = path.replace('/zh', '').replace('/en', '');

  console.log("Current location without language prefix:", currentLocation);

  useEffect(() => {
    const handleResize = () => window.innerWidth >= 1200 && (setIsOpen(false), setActiveDropdown(null));
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleDropdown = (name: string) =>
    setActiveDropdown(activeDropdown === name ? null : name);

  const closeMobileMenu = () => {
    setIsOpen(false);
    setActiveDropdown(null);
  };

  const smoothScrollToHash = (hash: string) => {
    const targetElement = document.querySelector(hash);
    if (targetElement instanceof HTMLElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleMenuLinkClick = (event: MouseEvent<HTMLAnchorElement>, href: string, isExternal?: boolean) => {
    closeMobileMenu();

    if (!href || isExternal) {
      return;
    }

    try {
      const targetUrl = new URL(href, window.location.origin);
      const { hash, pathname, origin } = targetUrl;

      if (origin !== window.location.origin || !hash) {
        return;
      }

      event.preventDefault();

      if (pathname !== window.location.pathname) {
        window.location.assign(`${pathname}${hash}`);
        return;
      }

      requestAnimationFrame(() => smoothScrollToHash(hash));
    } catch (error) {
      console.error('Navigation error', error);
    }
  };

  return (
    <nav className="navbar container">
      <div className="navbar-flex">
        <div className="navbar-logo">
          <a href={`${currentLanguage}`}>
            <img src="/images/logo-octopus.png" alt="八達科技有限公司 Octopus InfoTech Limited." />
          </a>
        </div>

        <button className="navbar-mobile-button" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu">
          <svg className="navbar-menu-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <ul className={`navbar-menu ${isOpen ? "open" : ""}`}>
          {navigationData?.data?.item && navigationData.data.item.map((item: any, index: number) => {
            if (item.__component === "menu.nav-link") {
              return (
                <li key={index} className={`${item.Display != true || item.FooterOnly == true ? "hidden" : ""}`}>
                  {(() => {
                    const href = `${currentLanguage}${item.Url ? item.Url : ''}`;
                    return (
                      <a
                        href={href}
                        target={item.isExternal ? "_blank" : "_self"}
                        className={`navbar-link ${currentLocation == item.Url ? "active" : ""} ${currentLocation == '' && item.Title == "Home" ? "active" : ""} `}
                        onClick={(event) => handleMenuLinkClick(event, href, item.isExternal)}
                      >
                        {item.Title ? item.Title : ''}
                      </a>
                    );
                  })()}
                </li>
              );
            } else {
              return (

                console.log("Navbar Dropdown Item", item),

                <li className={`navbar-dropdown ${item.Display != true || item.FooterOnly == true ? "hidden" : ""} ${currentLocation.includes(item.dropdownId) ? "active" : ""} `} key={index}>
                  <button onClick={() => toggleDropdown(item.dropdownId ? item.dropdownId : '')}
                    onFocus={() => setActiveDropdown(item.dropdownId ? item.dropdownId : '')}
                    className="navbar-dropdown-button">
                    <span>
                      {item.url ? (() => {
                        const href = `${currentLanguage}${item.url}`;
                        return (
                          <a href={href} onClick={(event) => handleMenuLinkClick(event, href, item.isExternal)}>
                            {item.title || ""}
                          </a>
                        );
                      })() : (item.title || "")}
                    </span>
                  </button>
                  <div className={`${item.dropdownId === "core-products" ? "sp-dropdown" : ""} navbar-dropdown-content ${activeDropdown === (item.dropdownId ? item.dropdownId : '') ? "open" : ""}`}
                    onFocus={() => setActiveDropdown(item.dropdownId ? item.dropdownId : '')}
                    onBlur={(e) => {
                      // Check if focus is moving outside the dropdown
                      if (!e.currentTarget.contains(e.relatedTarget)) {
                        setActiveDropdown(null);
                      }
                    }}
                    tabIndex={-1}
                  >
                    {item.subMenu && item.subMenu.map((subItem: any, subIndex: number) => (
                      <div key={subIndex} className={subItem.Display != true || item.FooterOnly == true ? "hidden" : ""}>
                        {(() => {
                          const href = `${currentLanguage}${subItem.url ? subItem.url : ''}`;
                          return (
                            <a
                              href={href}
                              target={item.isExternal ? "_blank" : "_self"}
                              className={`navbar-dropdown-item ${currentLocation == subItem.url ? "active" : ""}`}
                              onClick={(event) => handleMenuLinkClick(event, href, item.isExternal)}
                              onBlur={(e) => {
                                // Delay to check if focus moved outside dropdown
                                setTimeout(() => {
                                  const activeElement = document.activeElement;
                                  const dropdown = e.currentTarget.closest('.navbar-dropdown-content');
                                  if (dropdown && !dropdown.contains(activeElement)) {
                                    setActiveDropdown(null);
                                  }
                                }, 0);
                              }}
                            >
                              {subItem.title ? subItem.title : ''}
                            </a>
                          );
                        })()}
                        {subItem.Tier3.length > 0 && (
                          <ul>
                            {subItem.Tier3.map((tier3Item: any, tier3Index: number) => (
                              <li key={tier3Index} className={currentLocation == tier3Item.Url ? "active" : ""}>
                                {(() => {
                                  const href = `${currentLanguage}${tier3Item.Url ? tier3Item.Url : ''}`;
                                  return (
                                    <a
                                      href={href}
                                      target={item.isExternal ? "_blank" : "_self"}
                                      className={`navbar-dropdown-item tier3-item ${item.Display != true || item.FooterOnly == true ? "hidden" : ""} `}
                                      onClick={(event) => handleMenuLinkClick(event, href, item.isExternal)}
                                      onBlur={(e) => {
                                        // Delay to check if focus moved outside dropdown
                                        setTimeout(() => {
                                          const activeElement = document.activeElement;
                                          const dropdown = e.currentTarget.closest('.navbar-dropdown-content');
                                          if (dropdown && !dropdown.contains(activeElement)) {
                                            setActiveDropdown(null);
                                          }
                                        }, 0);
                                      }}
                                    >
                                      {tier3Item.Title ? tier3Item.Title : ''}
                                    </a>
                                  );
                                })()}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </li>
              );
            }
          })}
          <li><div className="mob-lang" onClick={handleLanguage}>{isEnglish ? "中" : "EN"}</div></li>
        </ul>
        <Switch isDefault={isEnglish} onToggle={handleLanguage} />
      </div>
    </nav>
  );
};

export default Navbar;
